import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface SetOperation {
  name: string;
  nameEn: string;
  symbol: string;
  description: string;
  formula: string;
  leftColor: string;
  rightColor: string;
  overlapColor: string;
  highlightMode: 'left' | 'right' | 'overlap' | 'all' | 'diff';
}

const OPERATIONS: SetOperation[] = [
  {
    name: '并集',
    nameEn: 'Union',
    symbol: 'A ∪ B',
    description: '属于A或属于B的所有元素',
    formula: 'A ∪ B = {x | x∈A 或 x∈B}',
    leftColor: '#4A90E2',
    rightColor: '#E07A5F',
    overlapColor: '#9B59B6',
    highlightMode: 'all',
  },
  {
    name: '交集',
    nameEn: 'Intersection',
    symbol: 'A ∩ B',
    description: '同时属于A和B的元素',
    formula: 'A ∩ B = {x | x∈A 且 x∈B}',
    leftColor: '#4A90E2',
    rightColor: '#E07A5F',
    overlapColor: '#FFD700',
    highlightMode: 'overlap',
  },
  {
    name: '差集',
    nameEn: 'Difference',
    symbol: 'A − B',
    description: '属于A但不属于B的元素',
    formula: 'A − B = {x | x∈A 且 x∉B}',
    leftColor: '#4A90E2',
    rightColor: '#E07A5F',
    overlapColor: '#2ECC71',
    highlightMode: 'diff',
  },
  {
    name: '对称差',
    nameEn: 'Symmetric Difference',
    symbol: 'A △ B',
    description: '属于A或B但不同时属于两者的元素',
    formula: 'A △ B = (A−B) ∪ (B−A)',
    leftColor: '#4A90E2',
    rightColor: '#E07A5F',
    overlapColor: '#1ABC9C',
    highlightMode: 'left',
  },
];

const VennDiagram: React.FC<{
  op: SetOperation;
  frame: number;
  phaseStart: number;
  fps: number;
}> = ({ op, frame, phaseStart, fps }) => {
  const localFrame = frame - phaseStart;
  const drawProg = spring({
    frame: localFrame - 5,
    fps,
    config: { damping: 14, stiffness: 100 },
  });
  const highlightProg = interpolate(localFrame, [25, 45], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const cx = 300;
  const cy = 200;
  const r = 140;
  const offset = 90;

  const getHighlightOpacity = (region: 'left' | 'overlap' | 'right') => {
    const base = 0.15;
    const active = 0.65 * highlightProg;
    switch (op.highlightMode) {
      case 'all':
        return base + active;
      case 'overlap':
        return region === 'overlap' ? base + active : base;
      case 'diff':
        return region === 'left' ? base + active : base;
      case 'left':
        return region === 'overlap' ? base : base + active;
      default:
        return base;
    }
  };

  return (
    <svg width={600} height={400} style={{ transform: `scale(${drawProg})` }}>
      <defs>
        <clipPath id="clipA">
          <circle cx={cx - offset} cy={cy} r={r} />
        </clipPath>
        <clipPath id="clipB">
          <circle cx={cx + offset} cy={cy} r={r} />
        </clipPath>
      </defs>

      <circle
        cx={cx - offset}
        cy={cy}
        r={r}
        fill={op.leftColor}
        opacity={getHighlightOpacity('left')}
        stroke={op.leftColor}
        strokeWidth={3}
      />
      <circle
        cx={cx + offset}
        cy={cy}
        r={r}
        fill={op.rightColor}
        opacity={getHighlightOpacity('right')}
        stroke={op.rightColor}
        strokeWidth={3}
      />

      <circle
        cx={cx + offset}
        cy={cy}
        r={r}
        fill={op.overlapColor}
        opacity={getHighlightOpacity('overlap')}
        clipPath="url(#clipA)"
      />

      <text x={cx - offset - 40} y={cy + 8} fontSize={36} fontWeight={700} fill="#fff">
        A
      </text>
      <text x={cx + offset + 20} y={cy + 8} fontSize={36} fontWeight={700} fill="#fff">
        B
      </text>

      <text
        x={cx}
        y={cy + r + 50}
        fontSize={32}
        fontWeight={700}
        fill={op.overlapColor}
        textAnchor="middle"
      >
        {op.symbol}
      </text>
    </svg>
  );
};

export const SetTheoryAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentIndex = Math.min(
    Math.floor(Math.max(0, frame - 50) / 120),
    OPERATIONS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: '#0B1120',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.06,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 60,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 68,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4A90E2, #9B59B6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          集合论可视化
        </h1>
        <p style={{ fontSize: 30, margin: '8px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          Set Theory · 数学的基石
        </p>
      </div>

      {OPERATIONS.map((op, i) => {
        const phaseStart = 50 + i * 120;
        const phaseEnd = phaseStart + 115;
        const fadeIn = interpolate(frame, [phaseStart, phaseStart + 20], [0, 1], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const fadeOut = interpolate(frame, [phaseEnd - 15, phaseEnd], [1, 0], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        });
        const opacity = i === OPERATIONS.length - 1 ? fadeIn : fadeIn * fadeOut;

        if (frame < phaseStart || opacity <= 0) return null;

        return (
          <div
            key={op.nameEn}
            style={{
              position: 'absolute',
              top: 310,
              left: 60,
              right: 60,
              opacity,
              display: 'flex',
              gap: 60,
              alignItems: 'center',
            }}
          >
            <VennDiagram op={op} frame={frame} phaseStart={phaseStart} fps={fps} />

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
                <h2 style={{ fontSize: 52, fontWeight: 700, color: op.overlapColor, margin: 0 }}>
                  {op.name}
                </h2>
                <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.35)' }}>{op.nameEn}</span>
              </div>

              <div
                style={{
                  padding: '20px 28px',
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: 14,
                  marginBottom: 24,
                  borderLeft: `4px solid ${op.overlapColor}`,
                }}
              >
                <span style={{ fontSize: 36, fontWeight: 700, color: '#FFD700', fontFamily: 'Georgia, serif' }}>
                  {op.formula}
                </span>
              </div>

              <p style={{ fontSize: 30, color: '#E5E7EB', lineHeight: 1.6, margin: 0 }}>
                {op.description}
              </p>

              <div
                style={{
                  marginTop: 30,
                  display: 'flex',
                  gap: 20,
                }}
              >
                {['A = {1,2,3,4,5}', 'B = {3,4,5,6,7}'].map((set, si) => (
                  <div
                    key={si}
                    style={{
                      padding: '14px 24px',
                      background: si === 0 ? `${op.leftColor}20` : `${op.rightColor}20`,
                      border: `1px solid ${si === 0 ? op.leftColor : op.rightColor}40`,
                      borderRadius: 12,
                      fontSize: 30,
                      color: si === 0 ? op.leftColor : op.rightColor,
                      fontFamily: 'Georgia, serif',
                    }}
                  >
                    {set}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 14,
        }}
      >
        {OPERATIONS.map((op, i) => (
          <div
            key={op.nameEn}
            style={{
              width: i === currentIndex ? 44 : 12,
              height: 12,
              borderRadius: 6,
              background: i === currentIndex ? op.overlapColor : 'rgba(255,255,255,0.15)',
            }}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};
